<template>
  <ListComponent title="Vendor Tariff">
    <template #list>
      <v-row class="ma-0">
        <v-col cols="4" class="d-flex align-center px-0 pt-2">
          <v-text-field
            label="Search"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="filters.search"
            @input="searchTransporterContracts"
          />

          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                icon
                v-on="on"
                v-bind="attrs"
                @click="showFilterDialog = true"
                class="mx-2"
              >
                <v-badge
                  dot
                  color="primary"
                  :value="Object.keys(nonEmptyFilters).length"
                >
                  <v-icon>mdi-filter</v-icon>
                </v-badge>
              </v-btn>
            </template>
            <span v-if="Object.keys(nonEmptyFilters).length === 0">Filters</span>
            <v-row v-else no-gutters>
              <v-col cols="12" class="font-weight-bold">Applied Filters</v-col>
              <v-col cols="12">
                <span
                  v-for="(value, key) in nonEmptyFilters"
                  :key="key"
                  class="text-capitalize"
                >
                  {{ key.replace("_", " ") }}<br />
                </span>
              </v-col>
            </v-row>
          </v-tooltip>

          <Columns
            :headers="headers"
            :gridColInstance="gridColumnApi"
            localStorageKey="transporterContractList"
          />
        </v-col>

        <v-col cols="4" class="px-0">
          <v-tabs height="35" v-model="tab" centered>
            <v-tabs-slider />
            <v-tab>Active</v-tab>
            <v-tab>Closed</v-tab>
          </v-tabs>
        </v-col>

        <v-col
          v-if="totalItems > 0"
          cols="4"
          class="d-flex justify-end align-center px-0 pt-2"
        >
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>

        <v-col cols="12" class="pa-0">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 192px)"
            class="ag-theme-alpine"
            id="transporterContractGrid"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            @columnMoved="$bus.$emit('syncPreferences')"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :suppressDragLeaveHidesColumns="true"
            :rowData="transporterContractList"
          />
        </v-col>
      </v-row>

      <DetailsParent :resizable="true" v-model="displayDrawer" :options="{ width: 1100, borderSize: 3 }">
        <template #details>
          <TransporterContractDetails
            :permissions="permissions"
            @submitResp="showConfirmationDialog"
            @refreshList="getTransporterContractList"
            :contractId="selectedContractId"
          />
        </template>
      </DetailsParent>

      <AdminTransporterContractFilters
        v-model="showFilterDialog"
        :bindingObject="filters"
        @resetFilters="resetFilters"
        @applyFilters="itemsPerPageChanged(20)"
      />

      <RejectionRemarksDialog
        v-model="showCloseRemarksForm"
        :formDetails="contractDetails"
        :isRequiredRemarks="true"
        fieldKey="close_remarks"
        label="Close Remarks"
        @submitRemarks="closeRemarksSubmit"
      />
    </template>

    <template #dialogs>
      <RenewContractDialog
        v-model="renewDialog"
        :formDetails="renewObject"
        :formErrors="renewObjectErrors"
        @submitRemarks="renewContract"
        @removeFieldError="renewObjectErrors = {}"
      />
      <TransporterContractAddDialog
        v-model="addDialog"
        @refreshList="getTransporterContractList"
      />
      <BulkVendorContractUploadDialog
        v-model="bulkUploadDialog"
        @refreshList="getTransporterContractList"
      />
    </template>

    <template #actions>
      <v-btn
        v-if="permissions && permissions.add"
        @click="bulkUploadDialog = true"
        color="primary"
        depressed
        outlined
        class="mr-2"
      >
        <v-icon>mdi-upload</v-icon>
        <span>Bulk Upload</span>
      </v-btn>
      <v-btn
        v-if="permissions && permissions.add"
        @click="addDialog = true"
        color="primary"
        depressed
      >
        <v-icon>mdi-plus</v-icon>
        <span>Add Tariff</span>
      </v-btn>
    </template>
  </ListComponent>
</template>

<script>
import Columns from "@/components/General/Columns.vue";
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import Pagination from "@/components/General/Pagination.vue";
import AdminTransporterContractButton from "@/components/AgGridComponents/AdminTransporterContractButton.vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import RenewContractDialog from "@/components/ModuleBased/dialogs/RenewContractDialog.vue";
import TransporterContractDetails from "@/components/ModuleBased/details/TransporterContractDetails.vue";
import DetailsParent from "@/components/General/DetailsParent.vue";
import AdminTransporterContractFilters from "@/components/ModuleBased/dialogs/filters/AdminTransporterContractFilters.vue";
import RejectionRemarksDialog from "@/components/ModuleBased/dialogs/RejectionRemarksDialog.vue";
import TransporterContractAddDialog from "@/components/ModuleBased/dialogs/TransporterContractAddDialog.vue";
import BulkVendorContractUploadDialog from "@/components/ModuleBased/dialogs/BulkVendorContractUploadDialog.vue";
import { isAuthenticated, hasRouteAccess, getPermissions } from "@/utils/functions";

export default {
  components: {
    ListComponent,
    AgGridVue,
    Pagination,
    AdminTransporterContractButton,
    agColumnHeader: CustomHeader,
    TransporterContractDetails,
    DetailsParent,
    AdminTransporterContractFilters,
    RenewContractDialog,
    RejectionRemarksDialog,
    TransporterContractAddDialog,
    BulkVendorContractUploadDialog,
    Columns,
  },
  data() {
    return {
      addDialog: false,
      bulkUploadDialog: false,
      renewDialog: false,
      renewObject: {},
      renewObjectErrors: {},
      showCloseRemarksForm: false,
      contractDetails: {},

      sorting: {},
      headers: [
        {
          headerName: "Tariff ID",
          field: "contract_id",
          sortable: true,
          minWidth: 120,
          cellRenderer: (params) => {
            return `<span style="color:#f07025;cursor:pointer;text-decoration:underline">${params.value}</span>`;
          },
          onCellClicked: (params) => {
            this.viewContract(params.data.id);
          },
        },
        {
          headerName: "Transporter Code",
          field: "transporter_code",
          tooltipField: "transporter_code",
          sortable: true,
          minWidth: 140,
        },
        {
          headerName: "Transporter",
          field: "transporter_name",
          tooltipField: "transporter_name",
          sortable: true,
          minWidth: 260,
        },
        {
          headerName: "Job Type",
          field: "job_type",
          sortable: true,
          minWidth: 100,
        },
        {
          headerName: "Start Date",
          field: "start_date",
          sortable: true,
          minWidth: 120,
          tooltipValueGetter: (params) => this.$globals.formatDate(params.value),
          valueFormatter: (params) => this.$globals.formatDate(params.value),
        },
        {
          headerName: "End Date",
          field: "end_date",
          sortable: true,
          minWidth: 120,
          tooltipValueGetter: (params) => this.$globals.formatDate(params.value),
          valueFormatter: (params) => this.$globals.formatDate(params.value),
        },
        {
          headerName: "Actions",
          field: "actions",
          pinned: "right",
          minWidth: 260,
          cellRendererFramework: "AdminTransporterContractButton",
        },
      ],
      transporterContractList: [],

      selectedContractId: null,
      displayDrawer: false,
      permissions: null,

      pageNo: 1,
      itemsPerPage: 20,
      totalItems: 0,

      filters: {},
      showFilterDialog: false,

      tab: 0,

      gridApi: null,
      gridColumnApi: null,
      defaultColDef: { resizable: true },
      gridOptions: {
        onGridSizeChanged: () => { this.resizeGrid(); },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
    nonEmptyFilters() {
      return Object.fromEntries(
        Object.entries(this.filters).filter(([, value]) => {
          if (typeof value === "object" && value !== null) {
            return Object.keys(value).length > 0;
          }
          return value;
        })
      );
    },
  },
  watch: {
    tab() {
      this.getTransporterContractList({ offset: 0, limit: this.itemsPerPage });
      this.displayDrawer = false;
    },
  },
  methods: {
    showConfirmationDialog() {
      this.showCloseRemarksForm = true;
    },
    showCloseDialog(id) {
      this.selectedContractId = id;
      this.showCloseRemarksForm = true;
    },
    closeRemarksSubmit() {
      this.$bus.$emit("showLoader", true);
      this.$api.transporterContract
        .closeTransporterContract({ id: this.selectedContractId, close_remarks: this.contractDetails.close_remarks })
        .then(() => {
          this.getTransporterContractList();
          this.$bus.$emit("showToastMessage", { message: "Tariff closed successfully!", color: "success" });
          this.showCloseRemarksForm = false;
          this.selectedContractId = null;
          this.displayDrawer = false;
          delete this.contractDetails.close_remarks;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showToastMessage", {
            message: err.data && err.data.non_field_errors ? err.data.non_field_errors.join() : "Couldn't close tariff!",
            color: "error",
          });
          this.$bus.$emit("showLoader", false);
        });
    },
    resizeGrid() {
      if (this.gridApi) {
        setTimeout(() => { this.gridApi.sizeColumnsToFit(); }, 100);
      }
    },
    resetFilters() {
      this.filters = {};
      this.itemsPerPageChanged(20);
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    searchTransporterContracts() {
      this.pageNo = 1;
      this.getTransporterContractList();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getTransporterContractList();
    },
    prevPage() {
      this.pageNo--;
      this.getTransporterContractList();
    },
    nextPage() {
      this.pageNo++;
      this.getTransporterContractList();
    },
    applyGridSort(key, type) {
      if (this.filters && !this.filters.ordering) {
        this.filters.ordering = [];
      }
      if (type == null) {
        delete this.sorting[key];
        this.filters.ordering.splice(this.filters.ordering.indexOf(`-${key}`), 1);
      } else if (type == "asc") {
        this.filters.ordering.push(key);
      } else if (type == "desc") {
        this.filters.ordering.splice(this.filters.ordering.indexOf(key), 1, `-${key}`);
      }
      if (this.filters.ordering.length == 0) {
        delete this.filters.ordering;
      }
      this.itemsPerPageChanged(20);
    },
    getTransporterContractList(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        status: this.tab == 0 ? "Active" : "Closed",
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.transporterContract
        .getTransporterContractList(params)
        .then((res) => {
          this.transporterContractList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    editContract(id) {
      this.$router.push({ path: `/app/admin/transporter-contract/edit/${id}`, query: { active_tab: this.tab } });
    },
    viewContract(id) {
      this.selectedContractId = id;
      this.displayDrawer = true;
    },
    checkQuery() {
      if ("active_tab" in this.$route.query) {
        this.tab = parseInt(this.$route.query.active_tab);
      }
    },
    showRenewDialog(id) {
      this.selectedContractId = id;
      this.renewDialog = true;
    },
    renewContract() {
      this.$api.transporterContract
        .renewContract({ id: this.selectedContractId, data: this.renewObject })
        .then(() => {
          this.renewDialog = false;
          this.getTransporterContractList();
          this.$bus.$emit("showToastMessage", { message: "Tariff Renewed Successfully!", color: "success" });
          this.$bus.$emit("showLoader", false);
          this.renewObject.start_date = null;
          this.renewObject.end_date = null;
        })
        .catch((err) => {
          console.error(err);
          this.renewObjectErrors = err.data;
          this.$bus.$emit("showToastMessage", {
            message: err.data && err.data.non_field_errors ? err.data.non_field_errors.join() : "Something went wrong!",
            color: "error",
          });
          this.$bus.$emit("showLoader", false);
        });
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("contract");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", { message: "You don't have access to this page", color: "error" });
        this.$router.push(`/app/${localStorage.getItem("user_type") ? localStorage.getItem("user_type").toLowerCase() : "error"}`);
      } else {
        this.permissions = { ...permissions };
        this.checkQuery();
        this.getTransporterContractList();
      }
    }
  },
};
</script>
